import api from "api";
import { BASE_API_PATH } from "api/wrapper";
import { Option } from "components/styled/async-search-select-v2/AsyncSearchSelectV2.types";
import { ApiParams } from "types/apiTypes";
import { NamedSettingsResourceConfig } from "../constants/healthSelectionResources";
import { NamedOption, NamedOptionAddress } from "../models/NamedOption";

// Build the nested `address_attributes` payload the backend expects, or return
// nothing when the resource has no address / the address is left blank.
const buildAddressAttributes = (address?: NamedOptionAddress | null) => {
  if (!address || !address.line1?.trim()) {
    return undefined;
  }

  return {
    ...(address.id ? { id: address.id } : {}),
    line1: address.line1,
    line2: address.line2 || null,
    city: address.city,
    state: address.state,
    zip: address.zip,
    country: address.country || "US",
  };
};

const buildQueryString = (params: ApiParams = {}) => {
  const query = new URLSearchParams();

  if (params.page !== undefined) {
    query.set("page", String(params.page));
  }

  if (params.size !== undefined) {
    query.set("size", String(params.size));
  }

  if (params.search !== undefined) {
    query.set("search", params.search);
  }

  if (params.sortBy?.length) {
    query.set("sort_by", params.sortBy.join(","));
  }

  return query.toString();
};

const parseErrorMessage = async (response: Response) => {
  try {
    const data = await response.json();

    if (Array.isArray(data?.errors)) {
      return data.errors
        .map((error) => error?.message || error?.detail || error?.title)
        .filter(Boolean)
        .join(", ");
    }

    if (typeof data?.error === "string") {
      return data.error;
    }
  } catch (error) {
    console.error("Could not parse error response", error);
  }

  return `Request failed (${response.status})`;
};

const authorizedRequest = async <T>(
  resource: NamedSettingsResourceConfig,
  path = "",
  init: RequestInit = {}
): Promise<{ data: T; response: Response }> => {
  const token = await api.auth.getAccessToken();
  const response = await fetch(`${BASE_API_PATH}/${resource.resourcePath}${path}`, {
    ...init,
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      ...(init.headers || {}),
    },
  });

  if (!response.ok) {
    throw new Error(await parseErrorMessage(response));
  }

  return {
    data: (await response.json()) as T,
    response,
  };
};

export const listNamedOptions = async (
  resource: NamedSettingsResourceConfig,
  params: ApiParams = {}
) => {
  const queryString = buildQueryString(params);
  const { data, response } = await authorizedRequest<NamedOption[]>(
    resource,
    queryString ? `?${queryString}` : ""
  );

  return {
    body: data,
    headers: {
      "total-count": response.headers.get("total-count") || String(data.length),
    },
  };
};

export const createNamedOption = async (
  resource: NamedSettingsResourceConfig,
  { name, address }: { name: string; address?: NamedOptionAddress | null }
) => {
  const addressAttributes = buildAddressAttributes(address);

  const { data } = await authorizedRequest<NamedOption>(resource, "", {
    method: "POST",
    body: JSON.stringify({
      [resource.bodyKey]: {
        name: name,
        ...(addressAttributes ? { address_attributes: addressAttributes } : {}),
      },
    }),
  });

  return data;
};

export const updateNamedOption = async (
  resource: NamedSettingsResourceConfig,
  {
    id,
    name,
    address,
  }: {
    id: string;
    name: string;
    address?: NamedOptionAddress | null;
  }
) => {
  const addressAttributes = buildAddressAttributes(address);

  const { data } = await authorizedRequest<NamedOption>(resource, `/${id}`, {
    method: "PATCH",
    body: JSON.stringify({
      [resource.bodyKey]: {
        name: name,
        ...(addressAttributes ? { address_attributes: addressAttributes } : {}),
      },
    }),
  });

  return data;
};

export const deleteNamedOption = async (
  resource: NamedSettingsResourceConfig,
  { id }: { id: string }
) => {
  const { data } = await authorizedRequest<NamedOption>(resource, `/${id}`, {
    method: "DELETE",
  });

  return data;
};

export const fetchNamedOptionChoices = async (
  resource: NamedSettingsResourceConfig,
  search: string
): Promise<Option[]> => {
  const response = await listNamedOptions(resource, {
    search: search,
    size: 50,
  });

  return response.body.map((item) => ({
    label: item.name,
    value: item.name,
  }));
};

export const createNamedOptionChoice = async (
  resource: NamedSettingsResourceConfig,
  name: string
) => {
  await createNamedOption(resource, { name });

  return { data: { id: name } };
};
